import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import TabsCst from "@components/TabsCst";
import AddItemsTabBody from "./AddItemsTabBody";
import { AddItemsConfirmEntry, AddItemsModalCstProps } from "./types";

function AddItemsModalCst<T>({
  open,
  title,
  selectionMode,
  tabs,
  initialTab,
  onClose,
  onConfirm,
  onCreate,
  sortOptions,
  defaultSort,
  excludeIdsByTab,
  confirmLabel,
  createLabel,
}: AddItemsModalCstProps<T>) {
  const { t } = useTranslation("add-items-modal");
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState<string>(
    initialTab ?? tabs[0]?.key ?? "",
  );
  const [sort, setSort] = useState<string>(defaultSort ?? "");

  const [selectedByTab, setSelectedByTab] = useState<
    Record<string, Map<string | number, T>>
  >({});

  useEffect(() => {
    if (!open) return;
    setActiveTab(initialTab ?? tabs[0]?.key ?? "");
    setSort(defaultSort ?? "");
    setSelectedByTab({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTab, defaultSort]);

  const currentTab = tabs.find((t) => t.key === activeTab) ?? tabs[0];
  const currentMap = selectedByTab[activeTab] ?? new Map<string | number, T>();
  const selectedIds = new Set<string | number>(currentMap.keys());

  const handleToggle = (item: T, next: boolean) => {
    if (!currentTab) return;
    const id = currentTab.getId(item);
    setSelectedByTab((prev) => {
      if (selectionMode === "single") {
        const map = new Map<string | number, T>();
        if (next) map.set(id, item);
        return { ...prev, [activeTab]: map };
      }
      const map = new Map(prev[activeTab] ?? new Map());
      if (next) map.set(id, item);
      else map.delete(id);
      return { ...prev, [activeTab]: map };
    });
  };

  const totalSelectedCount = Object.values(selectedByTab).reduce(
    (acc, m) => acc + m.size,
    0,
  );
  const canConfirm = totalSelectedCount > 0;

  const handleConfirm = () => {
    const entries: AddItemsConfirmEntry<T>[] = [];
    Object.entries(selectedByTab).forEach(([tabKey, map]) => {
      map.forEach((item) => entries.push({ item, tabKey }));
    });
    onConfirm(entries);
  };

  const handleCreate = () => {
    onCreate?.(activeTab);
  };

  const renderTabs = tabs.map((tabCfg) => ({
    key: tabCfg.key,
    label: tabCfg.label,
    icon: tabCfg.icon,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px 0",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <Box sx={{ padding: "8px 24px 0" }}>
        <TabsCst
          tabs={renderTabs}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </Box>

      <DialogContent
        sx={{
          padding: "16px 24px 16px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        {currentTab && (
          <AddItemsTabBody<T>
            key={currentTab.key}
            tab={currentTab}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggle={handleToggle}
            excludeIds={excludeIdsByTab?.[currentTab.key]}
            sortOptions={sortOptions}
            sort={sort}
            onSortChange={setSort}
            onCreate={onCreate ? handleCreate : undefined}
            createLabel={createLabel}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!canConfirm}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
        >
          {confirmLabel ?? String(t("confirm") ?? "Aggiungi")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddItemsModalCst;
export type {
  AddItemsModalCstProps,
  AddItemsTabConfig,
  AddItemsConfirmEntry,
  AddItemsSelectionMode,
  AddItemsTabFilterDef,
} from "./types";
